/*
 * ServicePartnerList.tsx
 * This Service Partner list Component is used for representing all the saved partners in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { CONFIG_GROUPS, defaultFilterParams } from "../../../utils/constants";
import {
  Button,
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import ServicePartnerForm from "./form";
import { TConfigParam } from "../../../types/config";
// import BulkUploadForm from "./bulk-upload/bulk-upload-form";
import CustomDrawer from "../../../components/drawer";
import { GetConfigParamDataList } from "../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../utils";
import ActionCard from "../../../components/actionCard/actionCard";
import { UserContext } from "../../../context";
import {
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */
export default function StatusReasonList1() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TConfigParam[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<TConfigParam | null>(
    null
  );

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TConfigParam, b: TConfigParam) =>
        a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: TConfigParam) =>
        record.name?.includes(value as string),
    },
    {
      title: "Status",
      dataIndex: "refParamUniqueId",
      key: "refParamUniqueId",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "refParamUniqueId"),
      sorter: (a: TConfigParam, b: TConfigParam) => {
        if (a.refParamUniqueId && b.refParamUniqueId) {
          return a.refParamUniqueId.localeCompare(b.refParamUniqueId);
        }
      },

      onFilter: (value: string | number | boolean, record: TConfigParam) =>
        record.refParamUniqueId?.includes(value as string),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (item: TConfigParam) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITSTATUSREASON
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={ServicePartnerForm} // Passing ServicePartner component
                    componentProps={{
                      id: item.id, // Pass necessary props for ServicePartner
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Status Reason Form"
                  />
                ) : null}
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          ...filterParams,
          searchText: filterParams.searchText,
          groupUniqueId: CONFIG_GROUPS.STATUSREASON,
          pageSize: -1,
        },
      };

      const response = await post(GetConfigParamDataList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TConfigParam, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          name: item.name,
          refParamUniqueId: item.refParamUniqueId,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(data.map((item: TConfigParam) => item.name))
        ).map((name) => ({ text: name, value: name }));
        const descriptionFilters = Array.from(
          new Set(data.map((item: TConfigParam) => item.refParamUniqueId))
        ).map((refParamUniqueId) => ({
          text: refParamUniqueId,
          value: refParamUniqueId,
        }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            if (col.key === "refParamUniqueId") {
              return { ...col, filters: descriptionFilters };
            }
            return col;
          })
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);


 const filteredData = data.filter((item) =>
   Object.values(item).some((val) =>
     val?.toString().toLowerCase().includes(searchText.toLowerCase())
   )
 );

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  // Return JSX structure for User Component
  return (
    <>
      {Utils.isUserHasAccess(
        context.user?.privilegeList,
        ePrivileges.VIEWSTATUSREASON
      ) ? (
        <>
          <div className="pageBody">
            <PageHeader
              icon={<IdcardOutlined />}
              title={`${filterParams?.totalRows} Total Status Reason`}
              size="large"
            >
              <Space>
                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.ADDSTATUSREASON
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <Button disabled={isLoading} type="primary">
                        Add
                      </Button>
                    }
                    component={ServicePartnerForm} // Passing Service Partner Form component
                    componentProps={{
                      id: 0, // Pass necessary props for ServicePartnerForm
                      onRefreshList: () => {
                        navigate("/dashboard/configration");
                        !Utils.hasDifference(
                          defaultFilterParams,
                          filterParams
                        ) && fetchData();
                        setFilterParams(defaultFilterParams);
                      },
                    }}
                    title="Service Partner Form"
                  />
                ) : null}
                <Input
                  placeholder="Filter by keywords"
                  value={searchText}
                  onChange={handleSearch}
                  style={{ width: 200 }}
                />
                <ExportToCSV
                  data={exportData}
                  headers={exportHeaders}
                  fileName={`status_reasons_${DateUtils.format(new Date())}.csv`}
                />
                <Tooltip title="Reset Filters">
                  <FilterOutlined onClick={handleResetAll} />
                </Tooltip>
                <Tooltip title="Configure Columns">
                  <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
                </Tooltip>
              </Space>
            </PageHeader>

            <div className="tableWrap">
              <Table
                columns={visibleColumns}
                dataSource={filteredData}
                loading={isLoading}
                pagination={{
                  // ...pagination,
                  position: ["bottomLeft"],
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total: any, range: any[]) =>
                    `Showing ${range[0]}-${range[1]} of ${total} total entries`,
                }}
                onChange={handleTableChange}
                bordered={false}
                size="small"
                sticky
                scroll={{
                  y: "calc(100vh - 310px)", // Adjust height dynamically
                }}
              />
            </div>
            <Drawer
              title="Customize Columns"
              placement="right"
              onClose={() => setDrawerVisible(false)}
              open={drawerVisible}
              style={{ transition: "transform 0.3s ease-in-out" }}
            >
              <ColumnManager
                columns={columns}
                initialColumns={initialColumns}
                onDragEnd={handleDragEnd}
                onToggleColumn={toggleColumn}
                onResetColumns={handleResetColumns}
                onSave={handleSaveColumns}
                onCancel={() => setDrawerVisible(false)}
              />
            </Drawer>
          </div>
        </>
      ) : null}
    </>
  );
}
